/**
 * Landing footer legal links, synced to the CMS.
 *
 * The template ships the three founding papers as static anchors, so crawlers
 * and a page whose backend is down still get a footer. Once GET /api/legal/pages
 * answers, the footer shows exactly the active documents: a founding paper still
 * under its seed title keeps its static anchor (and its translation), while a
 * renamed or added one gets an anchor with the CMS title and slug. Without this
 * the footer kept saying "Cookie Policy" after an admin renamed the document,
 * and asked for `cookie-policy` after the slug changed.
 */
import { keepsSeedTitle, sortLegalDocs, type LegalPageMeta } from '../game/legal-api';

export function syncLegalLinks(nav: HTMLElement, docs: LegalPageMeta[]): void {
  // Preview builds and an empty CMS answer []: keep the static fallback.
  if (docs.length === 0) return;
  const statics = [...nav.querySelectorAll<HTMLAnchorElement>('a[data-legal]')];
  if (statics.length === 0) return;
  const bySlug = new Map(statics.map((a) => [a.dataset.legal!, a]));

  const slot = document.createComment('legal-links');
  nav.insertBefore(slot, statics[0]);
  statics.forEach((a) => a.remove());

  const links = document.createDocumentFragment();
  for (const doc of sortLegalDocs(docs)) {
    const kept = bySlug.get(doc.slug);
    if (kept && keepsSeedTitle(doc)) {
      links.append(kept);
      continue;
    }
    const a = document.createElement('a');
    a.href = '#legal';
    a.dataset.legal = doc.slug;
    a.textContent = doc.title;
    links.append(a);
  }
  nav.insertBefore(links, slot);
  slot.remove();
}
